/**
 * Activity Status Business Logic Extensions Client - Pure Composition Pattern
 * 
 * Uses ActivityStatusesClient for basic CRUD operations
 * Adds business logic extensions for organization-specific endpoints
 * 
 * These methods call the business logic endpoints in:
 * server/routes/non_generated_single_table_business_logic_routes/activity-statuses-business-logic.ts
 */
import { ActivityStatusesClient } from '../generated_clients/generated-activity_statuses-api-client.js';
import type {
  ActivityStatuses,
  ActivityStatusesInsert,
  ActivityStatusesUpdate,
  ActivityStatusesQueryOptions
} from '../generated_schemas/generated-activity_statuses-schema.js';
import { httpClient } from '../http-client.js';

/**
 * Response type for organization-specific activity status endpoints
 * Matches the server response schema from activity-statuses-business-logic.ts
 */
interface ActivityStatusBusinessResponse {
  id: string;
  name: string;
  color: string | null;
  display_order: number;
  is_default: boolean;
  is_terminal: boolean;
}

/**
 * Response type for array of activity statuses
 */
interface ActivityStatusesArrayResponse {
  data: ActivityStatusBusinessResponse[];
  success: boolean;
}

/**
 * Response type for default activity status
 */
interface DefaultActivityStatusResponse {
  data: ActivityStatusBusinessResponse | null;
  success: boolean;
}

/**
 * Pure composition - no inheritance conflicts
 */
export class ActivityStatusExtensionsClient {
  constructor(private activityStatusesClient: ActivityStatusesClient) {}

  // Delegate all basic operations to ActivityStatusesClient
  async getAll(params?: ActivityStatusesQueryOptions): Promise<ActivityStatuses[]> {
    return this.activityStatusesClient.getAll(params);
  }

  async getById(id: string): Promise<ActivityStatuses> {
    return this.activityStatusesClient.getById(id);
  }

  async create(data: ActivityStatusesInsert): Promise<ActivityStatuses> {
    return this.activityStatusesClient.create(data);
  }

  async update(id: string, data: ActivityStatusesUpdate): Promise<ActivityStatuses> {
    return this.activityStatusesClient.update(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.activityStatusesClient.delete(id);
  }

  async count(filter?: Record<string, any>): Promise<number> {
    return this.activityStatusesClient.count(filter);
  }

  async exists(id: string): Promise<boolean> {
    return this.activityStatusesClient.exists(id);
  }

  async sort(
    sortBy: { column: string; ascending?: boolean }[],
    options?: { limit?: number; offset?: number }
  ): Promise<ActivityStatuses[]> {
    // Use the first sort column and call the business logic sort endpoint
    const firstSort = sortBy[0];
    if (!firstSort) {
      throw new Error('At least one sort column is required');
    }
    
    const order = firstSort.ascending === false ? 'desc' : 'asc';
    const response = await httpClient.get<{
      data: ActivityStatuses[];
      success: boolean;
    }>(`/activity-statuses/sort/${firstSort.column}?order=${order}`);
    
    return response.data;
  }

  async filter(
    filters: Record<string, any>,
    options?: { limit?: number; offset?: number }
  ): Promise<ActivityStatuses[]> {
    const response = await httpClient.post<{
      data: ActivityStatuses[];
      success: boolean;
    }>('/activity-statuses/filter', filters);
    
    return response.data;
  }

  // Business logic extension methods - these call the NEW endpoints

  /**
   * Get activity statuses by organization ID
   * Calls the business logic endpoint: /api/activity-statuses/organizations/:organizationId/activity-statuses
   *
   * This endpoint returns only active activity statuses, ordered by display_order
   */
  async getActivityStatusesByOrganization(orgId: string): Promise<ActivityStatusesArrayResponse> {
    this.log(`Getting activity statuses for organization: ${orgId}`);
    
    try {
      const response = await httpClient.get<ActivityStatusesArrayResponse>(
        `/activity-statuses/organizations/${orgId}/activity-statuses`
      );
      
      this.log(`Retrieved ${response.data.length} activity statuses for organization ${orgId}`);
      return response;
    } catch (error: any) {
      // Log error for debugging
      console.error('[ActivityStatusExtensions] Error fetching statuses:', error);
      
      // Re-throw with additional context
      throw new Error(`Failed to fetch activity statuses for organization ${orgId}: ${error.message}`);
    }
  }

  /**
   * Get default activity status for organization
   * Calls the business logic endpoint: /api/activity-statuses/organizations/:organizationId/activity-statuses/default
   */
  async getDefaultActivityStatus(orgId: string): Promise<DefaultActivityStatusResponse> {
    this.log(`Getting default activity status for organization: ${orgId}`);
    
    try {
      const response = await httpClient.get<DefaultActivityStatusResponse>(
        `/activity-statuses/organizations/${orgId}/activity-statuses/default`
      );
      
      this.log(`Retrieved default activity status for organization ${orgId}:`, response.data);
      return response;
    } catch (error: any) {
      // Log error for debugging
      console.error('[ActivityStatusExtensions] Error fetching default status:', error);
      
      // Re-throw with additional context
      throw new Error(`Failed to fetch default activity status for organization ${orgId}: ${error.message}`);
    }
  }


  /**
   * Get count of active activity statuses for an organization
   * Calls the business logic endpoint: /api/activity-statuses/organizations/:organizationId/activity-statuses/count
   */
  async getActivityStatusCountByOrganization(orgId: string): Promise<{ total: number; success: boolean }> {
    this.log(`Getting activity status count for organization: ${orgId}`);
    
    try {
      const response = await httpClient.get<{
        total: number;
        success: boolean;
      }>(`/activity-statuses/organizations/${orgId}/activity-statuses/count`);
      
      this.log(`Retrieved count of ${response.total} activity statuses for organization ${orgId}`);
      return response;
    } catch (error: any) {
      // Log error for debugging
      console.error('[ActivityStatusExtensions] Error fetching status count:', error);
      
      // Re-throw with additional context
      throw new Error(`Failed to fetch activity status count for organization ${orgId}: ${error.message}`);
    }
  }

  // Logging utility
  private log(message: string, data?: any): void {
    console.log(`[ActivityStatusExtensions] ${message}`, data || '');
  }
}

// Factory function
export const createActivityStatusExtensions = (activityStatusesClient: ActivityStatusesClient): ActivityStatusExtensionsClient =>
  new ActivityStatusExtensionsClient(activityStatusesClient);

// Export singleton
const activityStatusesClient = new ActivityStatusesClient();
export const activityStatusExtensions = createActivityStatusExtensions(activityStatusesClient);

// Export types for use in other parts of the application
export type {
  ActivityStatusBusinessResponse,
  ActivityStatusesArrayResponse,
  DefaultActivityStatusResponse
};